// The small bodies in their real shapes. The viewer builds each one as a
// quick procedural rock (createNEOs, createAsteroidBeltBodies in main.js)
// and registers it here; once the body is a few pixels across on screen its
// shape model, map and shading are fetched and swapped into the same mesh,
// so clicking, focus, orbits and the deep-space switch keep working on the
// object they always had. A body left far away for a while goes back to the
// placeholder and frees its maps.
//
// Shapes and maps: scripts/build-small-bodies.mjs, src/data/smallBodyModels.js.
// Shading: src/smallbodies/material.js. Sources: docs/textures/small-bodies.md.

import * as THREE from 'three';
import { SMALL_BODY_MODELS, SMALL_BODY_REL_MAX } from '../data/smallBodyModels.js';
import { rasterizeRadiusMap, fillRadiusHoles } from './radiusmap.js';
import { createSmallBodyMaterial, SMALL_BODY_RUBBLE, SMALL_BODY_REGOLITH } from './material.js';

const ASSET_VERSION = 1;
const LOAD_PX = 3;
const RELEASE_PX = 0.75;
const RELEASE_AFTER_MS = 60000;
const MAP_TEXEL = (2 * Math.PI) / 4096;

// Shading per body: the Lommel-Seeliger share, the map's relief, the
// procedural scales (cell sizes in mean radii, largest first; where the map
// has data only those too small for it to show), the mottling of an
// unimaged surface and the relief of the ground between rocks and craters.
// Rubble boulders fill about a third of a cell, craters about half.
const LOOK = {
  bennu: { ls: 0.7, bump: 0.0015, cells: [0.03, 0.012, 0.0045, 0.0018, 0.0007], mottle: 0.08, ground: 0.1 },
  ryugu: { ls: 0.7, bump: 0.0012, cells: [0.03, 0.012, 0.0045, 0.0018, 0.0007], mottle: 0.08, ground: 0.1 },
  eros: { ls: 0.55, bump: 0.0, cells: [0.03, 0.012, 0.0048, 0.0019, 0.0008], mottle: 0, ground: 0.06 },
  vesta: { ls: 0.5, bump: 0.002, cells: [0.04, 0.016, 0.0045, 0.0018, 0.0007], mottle: 0.06, ground: 0.06 },
  pallas: { ls: 0.6, bump: 0, cells: [0.05, 0.02, 0.008, 0.0032, 0.0013, 0.0005], mottle: 0.12, ground: 0.07 },
  hygiea: { ls: 0.7, bump: 0, cells: [0.05, 0.02, 0.008, 0.0032, 0.0013, 0.0005], mottle: 0.08, ground: 0.07 },
  apophis: { ls: 0.45, bump: 0, cells: [0.05, 0.02, 0.008, 0.0032, 0.0013, 0.0005], mottle: 0.14, ground: 0.1 },
};

const registry = [];
const config = {
  mobile: false,
  renderer: null,
  sunPosition: (v) => v.set(0, 0, 0),
  onSwap: () => {},
};

export function initSmallBodies(options = {}) {
  Object.assign(config, options);
}

export function smallBodyModel(name) {
  return SMALL_BODY_MODELS[String(name || '').toLowerCase()] || null;
}

// The stand-in drawn until the model arrives (and if it never does): a smooth
// lump with the model's proportions and the body's albedo.
export function smallBodyPlaceholderGeometry(entry, radius) {
  const geometry = new THREE.SphereGeometry(1, 64, 40);
  const p = geometry.getAttribute('position');
  const [ax, ay, az] = entry.axes || [1, 1, 1];
  const k = radius / Math.cbrt(ax * ay * az);
  const s = entry.id.length;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const lump = 1 + 0.06 * Math.sin(3 * x + s) * Math.sin(4 * y + 0.3 * s) * Math.sin(3.5 * z + 2.1)
      + 0.03 * Math.sin(7 * x + 1.3) * Math.sin(6 * z + s);
    p.setXYZ(i, x * ax * k * lump, y * ay * k * lump, z * az * k * lump);
  }
  geometry.computeVertexNormals();
  return geometry;
}

export function smallBodyPlaceholderMaterial(entry) {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color().setRGB(entry.display * entry.tint[0], entry.display * entry.tint[1], entry.display * entry.tint[2]),
    roughness: 1,
    metalness: 0,
  });
}

// mesh: the placeholder mesh the viewer clicks and focuses. owner: the object
// holding the body's userData (the mesh itself, or the belt body's group).
export function registerSmallBody(mesh, { radius, owner = mesh } = {}) {
  const entry = smallBodyModel(owner.userData.name || mesh.name);
  if (!entry || registry.some((r) => r.mesh === mesh)) return null;
  const rec = {
    mesh,
    owner,
    entry,
    radius,
    state: 'idle',
    placeholder: { geometry: mesh.geometry, material: mesh.material },
    loaded: null,
    farSince: 0,
  };
  owner.userData.smallBodyModel = entry.id;
  // The zoom floor keeps the camera outside the long axis (bodyShapeReach).
  owner.userData.shapeReach = entry.reach;
  registry.push(rec);
  return rec;
}

// ---------- orientation ----------

const _Y = new THREE.Vector3(0, 1, 0);
const _X = new THREE.Vector3(1, 0, 0);
const _pole = new THREE.Vector3();
const _qPole = new THREE.Quaternion();
const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();

// The body on its real spin pole (scene frame) turned by `angles`: [spin], or
// for a tumbler [precession, rotation] about the angular momentum and the
// body's own axis, with its nutation angle between them.
export function orientSmallBody(object, entry, angles) {
  _pole.fromArray(entry.pole).normalize();
  _qPole.setFromUnitVectors(_Y, _pole);
  if (entry.tumble) {
    _qa.setFromAxisAngle(_Y, angles[0]);
    _qb.setFromAxisAngle(_X, THREE.MathUtils.degToRad(entry.tumble.theta));
    _qa.multiply(_qb);
    _qb.setFromAxisAngle(_Y, angles[1]);
    _qa.multiply(_qb);
  } else {
    _qa.setFromAxisAngle(_Y, angles[0]);
  }
  object.quaternion.multiplyQuaternions(_qPole, _qa);
}

// ---------- loading ----------

let gltfLoaderPromise = null;
function gltfLoader() {
  if (!gltfLoaderPromise) {
    gltfLoaderPromise = (async () => {
      const [{ GLTFLoader }, meshoptModule] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/libs/meshopt_decoder.module.js'),
      ]);
      const decoder = meshoptModule.MeshoptDecoder || meshoptModule.default || meshoptModule;
      if (decoder && decoder.ready) await decoder.ready;
      const loader = new GLTFLoader();
      loader.setMeshoptDecoder(decoder);
      return loader;
    })();
  }
  return gltfLoaderPromise;
}

// Positions in mean radii, Y up, and the index, out of the GLB.
async function loadShape(entry) {
  const loader = await gltfLoader();
  const gltf = await loader.loadAsync(`${entry.model}?v=${ASSET_VERSION}`);
  let source = null;
  gltf.scene.traverse((n) => { if (n.isMesh && !source) source = n; });
  if (!source) throw new Error(`${entry.id}: no mesh in model`);
  source.updateWorldMatrix(true, false);
  const attr = source.geometry.getAttribute('position');
  const positions = new Float32Array(attr.count * 3);
  const v = new THREE.Vector3();
  for (let i = 0; i < attr.count; i++) {
    v.fromBufferAttribute(attr, i).applyMatrix4(source.matrixWorld);
    positions[i * 3] = v.x; positions[i * 3 + 1] = v.y; positions[i * 3 + 2] = v.z;
  }
  const index = source.geometry.index.array.slice();
  source.geometry.dispose();
  return { positions, index };
}

// The map as a texture with its top row at v = 0 (north), single channel for
// a grey map so it costs a quarter of RGBA on the GPU.
async function loadMap(entry) {
  const url = `${config.mobile ? entry.mobileMap : entry.map}?v=${ASSET_VERSION}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: ${res.status}`);
  const bitmap = await createImageBitmap(await res.blob(), { colorSpaceConversion: 'none', premultiplyAlpha: 'none' });
  const { width, height } = bitmap;
  const canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(width, height) : Object.assign(document.createElement('canvas'), { width, height });
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close && bitmap.close();
  const rgba = ctx.getImageData(0, 0, width, height).data;
  let texture;
  if (entry.mapColour) {
    texture = new THREE.DataTexture(new Uint8Array(rgba.buffer, rgba.byteOffset, rgba.byteLength), width, height, THREE.RGBAFormat, THREE.UnsignedByteType);
    texture.colorSpace = THREE.SRGBColorSpace;
  } else {
    const grey = new Uint8Array(width * height);
    for (let i = 0, j = 0; i < grey.length; i++, j += 4) grey[i] = rgba[j];
    texture = new THREE.DataTexture(grey, width, height, THREE.RedFormat, THREE.UnsignedByteType);
  }
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.flipY = false;
  texture.unpackAlignment = 1;
  if (config.renderer) texture.anisotropy = Math.min(8, config.renderer.capabilities.getMaxAnisotropy());
  texture.needsUpdate = true;
  return texture;
}

function radiusTextureFrom(radii, width, height) {
  const toHalf = (src) => {
    const half = new Uint16Array(src.length);
    for (let i = 0; i < src.length; i++) half[i] = THREE.DataUtils.toHalfFloat(src[i]);
    return half;
  };
  // Coarser levels for the shadow march's longer steps, each the mean of the
  // four below it, so a boulder far down the ray softens into a penumbra
  // instead of being stepped over.
  const mipmaps = [{ data: toHalf(radii), width, height }];
  let src = radii, w = width, h = height;
  while (w > 1 && h > 1) {
    const nw = w >> 1, nh = h >> 1;
    const out = new Float32Array(nw * nh);
    for (let j = 0; j < nh; j++) for (let i = 0; i < nw; i++) {
      const a = src[2 * j * w + 2 * i], b = src[2 * j * w + 2 * i + 1];
      const c = src[(2 * j + 1) * w + 2 * i], d = src[(2 * j + 1) * w + 2 * i + 1];
      out[j * nw + i] = 0.25 * (a + b + c + d);
    }
    mipmaps.push({ data: toHalf(out), width: nw, height: nh });
    src = out; w = nw; h = nh;
  }
  const texture = new THREE.DataTexture(mipmaps[0].data, width, height, THREE.RedFormat, THREE.HalfFloatType);
  texture.mipmaps = mipmaps;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.unpackAlignment = 2;
  texture.needsUpdate = true;
  return texture;
}

// Radius by direction off the grid, bilinear.
function radiusSampler(radii, width, height) {
  return (x, y, z) => {
    const r = Math.hypot(x, y, z) || 1;
    let lon = Math.atan2(-z, x);
    if (lon < 0) lon += Math.PI * 2;
    const lat = Math.asin(Math.max(-1, Math.min(1, y / r)));
    const fx = (lon / (Math.PI * 2)) * width - 0.5;
    const fy = Math.max(0, Math.min(height - 1, ((Math.PI / 2 - lat) / Math.PI) * height - 0.5));
    const x0 = Math.floor(fx), y0 = Math.floor(fy);
    const tx = fx - x0, ty = fy - y0;
    const xa = ((x0 % width) + width) % width, xb = (xa + 1) % width;
    const y1 = Math.min(height - 1, y0 + 1);
    const at = (i, j) => radii[j * width + i];
    return (at(xa, y0) * (1 - tx) + at(xb, y0) * tx) * (1 - ty) + (at(xa, y1) * (1 - tx) + at(xb, y1) * tx) * ty;
  };
}

// A light stand-in for picking: a geodesic sphere pushed out to the body's
// radius in every direction, so a click costs five thousand triangles, not
// sixty thousand.
function pickProxy(sample, radius) {
  const geometry = new THREE.IcosahedronGeometry(1, 4);
  const p = geometry.getAttribute('position');
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const r = sample(x, y, z) * radius;
    p.setXYZ(i, x * r, y * r, z * r);
  }
  geometry.computeBoundingSphere();
  return new THREE.Mesh(geometry, new THREE.MeshBasicMaterial());
}

const _sun = new THREE.Vector3();
const _inv = new THREE.Matrix4();
const _mv = new THREE.Matrix4();
const _scale = new THREE.Vector3();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();

function bindFrame(rec) {
  const { mesh } = rec;
  const u = rec.loaded.material.userData.sbUniforms;
  mesh.onBeforeRender = (renderer, scene, camera) => {
    config.sunPosition(_sun);
    _inv.copy(mesh.matrixWorld).invert();
    u.sbSunObj.value.copy(_sun).applyMatrix4(_inv).normalize();
    u.sbSunView.value.copy(_sun).applyMatrix4(camera.matrixWorldInverse);
    _mv.multiplyMatrices(camera.matrixWorldInverse, mesh.matrixWorld);
    _mv.decompose(_pos, _quat, _scale);
    _mv.makeRotationFromQuaternion(_quat);
    u.sbObjToView.value.setFromMatrix4(_mv);
  };
}

async function load(rec) {
  rec.state = 'loading';
  const { entry } = rec;
  try {
    const [shape, map] = await Promise.all([loadShape(entry), entry.map ? loadMap(entry) : Promise.resolve(null)]);
    if (rec.state !== 'loading') { if (map) map.dispose(); return; }
    const rw = config.mobile ? 512 : 1024;
    const rh = rw / 2;
    const radii = fillRadiusHoles(rasterizeRadiusMap(shape.positions, shape.index, rw, rh), rw, rh);
    const radiusTexture = radiusTextureFrom(radii, rw, rh);
    const scaled = new Float32Array(shape.positions.length);
    for (let i = 0; i < scaled.length; i++) scaled[i] = shape.positions[i] * rec.radius;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(scaled, 3));
    geometry.setIndex(new THREE.BufferAttribute(shape.index, 1));
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    const look = LOOK[entry.id] || LOOK.pallas;
    let cells = look.cells;
    if (config.mobile) cells = cells.filter((c, i) => i % 2 === 0).slice(0, 3);
    const albedo = new THREE.Color().setRGB(entry.display * entry.tint[0], entry.display * entry.tint[1], entry.display * entry.tint[2]);
    const material = createSmallBodyMaterial({
      albedo,
      map,
      mapGrey: !!map && !entry.mapColour,
      relMax: SMALL_BODY_REL_MAX,
      validLat: entry.mapValidLat ? entry.mapValidLat.map((d) => THREE.MathUtils.degToRad(d)) : null,
      radiusTexture,
      reach: entry.reach,
      scale: rec.radius,
      ls: look.ls,
      bump: map ? look.bump * (((2 * Math.PI) / map.image.width) / MAP_TEXEL) : 0,
      cells,
      mapTexel: map ? (2 * Math.PI) / map.image.width : 0,
      mottle: look.mottle,
      ground: look.ground,
      seed: entry.id.length * 7.31,
      style: entry.style === 'rubble' ? SMALL_BODY_RUBBLE : SMALL_BODY_REGOLITH,
      mobile: config.mobile,
    });
    const proxy = pickProxy(radiusSampler(radii, rw, rh), rec.radius);
    rec.loaded = { geometry, material, map, radiusTexture, proxy };
    const { mesh } = rec;
    mesh.geometry = geometry;
    mesh.material = material;
    mesh.raycast = function (raycaster, intersects) {
      proxy.matrixWorld.copy(this.matrixWorld);
      const hits = [];
      proxy.raycast(raycaster, hits);
      for (const hit of hits) { hit.object = this; intersects.push(hit); }
    };
    bindFrame(rec);
    rec.state = 'ready';
    config.onSwap(mesh, rec.owner);
  } catch (err) {
    console.warn(`[smallbodies] ${entry.id}: keeping the placeholder`, err);
    rec.state = 'failed';
  }
}

function release(rec) {
  const { mesh, loaded } = rec;
  rec.state = 'idle';
  rec.farSince = 0;
  if (!loaded) return;
  mesh.geometry = rec.placeholder.geometry;
  mesh.material = rec.placeholder.material;
  delete mesh.raycast;
  mesh.onBeforeRender = THREE.Object3D.prototype.onBeforeRender;
  loaded.geometry.dispose();
  loaded.material.dispose();
  if (loaded.map) loaded.map.dispose();
  loaded.radiusTexture.dispose();
  loaded.proxy.geometry.dispose();
  loaded.proxy.material.dispose();
  rec.loaded = null;
  config.onSwap(mesh, rec.owner);
}

function shown(object) {
  for (let o = object; o; o = o.parent) if (!o.visible) return false;
  return true;
}

const _world = new THREE.Vector3();
const _ws = new THREE.Vector3();

// Once a frame: fetch the bodies grown big enough to see, free the ones left
// far away. viewportHeight in CSS or device pixels, as long as it matches.
export function updateSmallBodies(camera, viewportHeight, now = performance.now()) {
  if (!registry.length || !camera) return;
  const focal = viewportHeight / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov || 45) / 2));
  for (const rec of registry) {
    let px = 0;
    if (shown(rec.mesh)) {
      rec.mesh.getWorldPosition(_world);
      rec.mesh.getWorldScale(_ws);
      const dist = Math.max(1e-9, camera.position.distanceTo(_world));
      px = (rec.radius * _ws.x * rec.entry.reach) / dist * focal;
    }
    if (rec.state === 'idle' && px > LOAD_PX) load(rec);
    else if (rec.state === 'ready') {
      if (px < RELEASE_PX) {
        if (!rec.farSince) rec.farSince = now;
        else if (now - rec.farSince > RELEASE_AFTER_MS) release(rec);
      } else rec.farSince = 0;
    }
  }
}

// For checks: window.__smallBodies in main.js.
export function smallBodyStates() {
  return registry.map((r) => ({ id: r.entry.id, state: r.state }));
}

export function loadSmallBodyNow(name) {
  const rec = registry.find((r) => r.entry.id === String(name || '').toLowerCase());
  if (rec && rec.state === 'idle') load(rec);
  return rec ? rec.state : null;
}

export function smallBodyRecord(name) {
  const rec = registry.find((r) => r.entry.id === String(name || '').toLowerCase());
  return rec ? { mesh: rec.mesh, owner: rec.owner, radius: rec.radius, state: rec.state, entry: rec.entry } : null;
}
